import { useMemo, useState, useCallback } from "react";
import {
  Card,
  Empty,
  Typography,
  Space,
  Tag,
  Tooltip,
  Badge,
  Button,
  Popover,
  Divider,
  Row,
  Col,
  Alert,
} from "antd";
import {
  EnvironmentOutlined,
  ShopOutlined,
  DollarOutlined,
  ClockCircleOutlined,
  CalendarOutlined,
  InfoCircleOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import { CrossMerchantConsumer } from "../../../interfaces/crossMerchantAnalytics.interface";
import { GoogleMap, Marker, InfoWindow, useLoadScript, Polyline } from "@react-google-maps/api";
import { CONFIG } from "../../../config";
import styles from "./InteractiveMap.module.css";

const { Text, Title } = Typography;

interface MerchantMarker {
  consumer: CrossMerchantConsumer;
  merchant: CrossMerchantConsumer["merchants"][0];
}

interface InteractiveMapProps {
  merchants: Array<{
    consumer: CrossMerchantConsumer;
    merchant: CrossMerchantConsumer["merchants"][0];
  }>;
  selectedConsumer: string | null;
  onMarkerClick?: (merchant: MerchantMarker) => void;
  showJourneyPath?: boolean;
}

export default function InteractiveMap({
  merchants,
  selectedConsumer,
  onMarkerClick,
  showJourneyPath = false,
}: InteractiveMapProps) {
  const [selectedMarker, setSelectedMarker] = useState<MerchantMarker | null>(
    null
  );

  // Load Google Maps script
  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: CONFIG.GOOGLE_MAPS.API_KEY,
  });

  // Calculate map center - default to Amsterdam city center, adjust only when consumer is selected
  const mapCenter = useMemo(() => {
    const merchantsWithCoords = merchants.filter(m => m.merchant.coordinates !== null);
    
    // Amsterdam city center coordinates
    const amsterdamCenter = { lat: 52.3676, lng: 4.9041 };
    
    // If no merchants, always return Amsterdam
    if (merchantsWithCoords.length === 0) {
      return amsterdamCenter;
    }
    
    // If consumer is selected, calculate center from their merchants
    if (selectedConsumer) {
      const consumerMerchants = merchantsWithCoords.filter(
        m => m.consumer.paymentAccountReference === selectedConsumer
      );
      
      if (consumerMerchants.length > 0) {
        const avgLat =
          consumerMerchants.reduce(
            (sum, item) => sum + (item.merchant.coordinates?.lat || 0),
            0
          ) / consumerMerchants.length;
        const avgLng =
          consumerMerchants.reduce(
            (sum, item) => sum + (item.merchant.coordinates?.lng || 0),
            0
          ) / consumerMerchants.length;
        
        // Return calculated center if valid, otherwise Amsterdam
        if (!isNaN(avgLat) && !isNaN(avgLng)) {
          return { lat: avgLat, lng: avgLng };
        }
      }
    }
    
    // Default: Always show Amsterdam city center
    return amsterdamCenter;
  }, [merchants, selectedConsumer]);
  
  // Calculate zoom level - default to 12 (shows Amsterdam city center), adjust for selected consumer
  const mapZoom = useMemo(() => {
    const merchantsWithCoords = merchants.filter(m => m.merchant.coordinates !== null);
    
    // Default zoom to show Amsterdam city center as a whole
    const defaultZoom = 12;
    
    // If no merchants, use default zoom
    if (merchantsWithCoords.length === 0) {
      return defaultZoom;
    }
    
    // If consumer is selected, adjust zoom to fit their merchants
    if (selectedConsumer) {
      const consumerMerchants = merchantsWithCoords.filter(
        m => m.consumer.paymentAccountReference === selectedConsumer
      );
      
      if (consumerMerchants.length === 1) {
        return 15; // Close zoom for single merchant
      } else if (consumerMerchants.length > 1) {
        return 13; // Medium zoom for multiple merchants
      }
    }
    
    // Default: Show Amsterdam city center
    return defaultZoom;
  }, [merchants, selectedConsumer]);

  // Group merchants by consumer for journey path
  const merchantsByConsumer = useMemo(() => {
    const grouped = new Map<string, MerchantMarker[]>();
    merchants.forEach((item) => {
      const par = item.consumer.paymentAccountReference;
      if (!grouped.has(par)) {
        grouped.set(par, []);
      }
      grouped.get(par)!.push(item);
    });
    return grouped;
  }, [merchants]);

  const formatCurrency = (amount: number) => {
    if (typeof amount !== "number" || isNaN(amount)) {
      return "€0.00";
    }
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "EUR",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  const formatDateTime = (dateString: string) => {
    if (!dateString) return "N/A";
    const date = dayjs(dateString);
    if (!date.isValid()) return "Invalid Date";
    return date.format("MMM DD, YYYY HH:mm");
  };

  // Get marker color based on visit count
  // Note: Color coding is relative to the data in the current view
  const getMarkerColor = (visitCount: number) => {
    if (visitCount >= 10) return "#52c41a"; // Green - many visits
    if (visitCount >= 5) return "#1890ff"; // Blue - several visits
    if (visitCount >= 2) return "#faad14"; // Orange - few visits
    return "#ff4d4f"; // Red - single visit
  };

  // Get marker size based on transaction count
  const getMarkerSize = (count: number) => {
    if (count >= 20) return "large";
    if (count >= 10) return "medium";
    return "small";
  };

  const handleMarkerClick = useCallback(
    (merchant: MerchantMarker) => {
      setSelectedMarker(merchant);
      if (onMarkerClick) {
        onMarkerClick(merchant);
      }
    },
    [onMarkerClick]
  );

  // Show loading or error state
  if (loadError) {
    return (
      <div className={styles.map_container}>
        <Alert
          message="Error loading Google Maps"
          description="Please check your internet connection and try again."
          type="error"
          showIcon
        />
      </div>
    );
  }

  if (!isLoaded) {
    return (
      <div className={styles.map_container}>
        <div style={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "400px" }}>
          <Text>Loading map...</Text>
        </div>
      </div>
    );
  }

  // Fallback to list view if no coordinates
  if (merchants.length === 0 || !merchants.some(m => m.merchant.coordinates)) {
    return (
      <div className={styles.map_container}>
        <Empty
          description="No merchants with coordinates available"
          image={Empty.PRESENTED_IMAGE_SIMPLE}
        />

        {/* Merchant markers list */}
        <div className={styles.markers_list}>
          <Title level={5}>
            Merchant Locations ({merchants.length})
          </Title>
          <Space
            direction="vertical"
            size="small"
            style={{ width: "100%", maxHeight: "500px", overflowY: "auto" }}
          >
            {merchants.map((item, idx) => {
              const { consumer, merchant } = item;
              const markerColor = getMarkerColor(merchant.visitCount || 0);
              const isSelected = selectedMarker?.merchant.businessId === merchant.businessId;

              return (
                <Card
                  key={idx}
                  size="small"
                  style={{
                    marginBottom: 8,
                    border: isSelected ? `2px solid ${markerColor}` : undefined,
                    cursor: "pointer",
                  }}
                  onClick={() => handleMarkerClick(item)}
                  hoverable
                >
                  <div className={styles.merchant_card_mobile}>
                    {/* First line: Name only */}
                    <div className={styles.merchant_name_line}>
                      <div
                        className={styles.marker_dot}
                        style={{
                          backgroundColor: markerColor,
                        }}
                      />
                      <Tooltip title={merchant.businessTradeName}>
                        <Text strong className={styles.merchant_name_full}>
                          {merchant.businessTradeName}
                        </Text>
                      </Tooltip>
                    </div>

                    {/* Second line: City and Visit count */}
                    <div className={styles.merchant_meta_line}>
                      {merchant.city && (
                        <Tag icon={<EnvironmentOutlined />} color="blue" className={styles.city_tag}>
                          {merchant.city}
                        </Tag>
                      )}
                      <Tag color={markerColor} className={styles.visit_count_tag}>
                        {merchant.visitCount || 0} visits
                      </Tag>
                    </div>

                    {/* Rest: Coordinates, Transactions, etc. */}
                    <Row gutter={[8, 4]}>
                      <Col span={24}>
                        <Text type="secondary" style={{ fontSize: "11px" }}>
                          📍 {merchant.coordinates!.lat.toFixed(6)},{" "}
                          {merchant.coordinates!.lng.toFixed(6)}
                        </Text>
                      </Col>
                      <Col span={12}>
                        <Text type="secondary">Transactions:</Text>
                        <br />
                        <Text strong>{merchant.transactionCount}</Text>
                      </Col>
                      <Col span={12}>
                        <Text type="secondary">Total:</Text>
                        <br />
                        <Text strong style={{ color: "#3f8600" }}>
                          {formatCurrency(merchant.totalAmount)}
                        </Text>
                      </Col>
                      {merchant.firstVisit && (
                        <Col span={24}>
                          <Text type="secondary" style={{ fontSize: "11px" }}>
                            <CalendarOutlined /> First:{" "}
                            {formatDateTime(merchant.firstVisit)} | Last:{" "}
                            {merchant.lastVisit
                              ? formatDateTime(merchant.lastVisit)
                              : "N/A"}
                          </Text>
                        </Col>
                      )}
                      <Col span={24}>
                        <Text type="secondary" style={{ fontSize: "10px" }}>
                          PAR: {consumer.paymentAccountReference.substring(0, 30)}...
                      </Text>
                    </Col>
                  </Row>
                </div>
              </Card>
              );
            })}
          </Space>
        </div>

      {/* Selected marker details - Only show when marker has no coordinates (InfoWindow not available) */}
      {selectedMarker && !selectedMarker.merchant.coordinates && (
        <Card
          className={styles.marker_details}
          title={
            <Space>
              <ShopOutlined />
              <Text strong>{selectedMarker.merchant.businessTradeName}</Text>
            </Space>
          }
          extra={
            <Button
              type="text"
              onClick={() => setSelectedMarker(null)}
              size="small"
            >
              Close
            </Button>
          }
        >
            <Space direction="vertical" size="middle" style={{ width: "100%" }}>
              <div>
                <Text type="secondary">Business Name:</Text>
                <br />
                <Text strong>{selectedMarker.merchant.businessName}</Text>
              </div>
              <div>
                <Text type="secondary">Location:</Text>
                <br />
                <Text>
                  {selectedMarker.merchant.city && (
                    <Tag icon={<EnvironmentOutlined />}>
                      {selectedMarker.merchant.city}
                    </Tag>
                  )}
                  {selectedMarker.merchant.address && (
                    <Text type="secondary" style={{ fontSize: "12px" }}>
                      {[
                        selectedMarker.merchant.address.street,
                        selectedMarker.merchant.address.houseNumber,
                      ]
                        .filter(Boolean)
                        .join(" ")}
                    </Text>
                  )}
                </Text>
              </div>
              <Row gutter={16}>
                <Col span={12}>
                  <Text type="secondary">Transactions:</Text>
                  <br />
                  <Text strong>{selectedMarker.merchant.transactionCount}</Text>
                </Col>
                <Col span={12}>
                  <Text type="secondary">Total Amount:</Text>
                  <br />
                  <Text strong style={{ color: "#3f8600" }}>
                    {formatCurrency(selectedMarker.merchant.totalAmount)}
                  </Text>
                </Col>
                <Col span={12}>
                  <Text type="secondary">Avg Transaction:</Text>
                  <br />
                  <Text>
                    {formatCurrency(
                      selectedMarker.merchant.averageTransactionValue
                    )}
                  </Text>
                </Col>
                <Col span={12}>
                  <Text type="secondary">Visits:</Text>
                  <br />
                  <Tag color={getMarkerColor(selectedMarker.merchant.visitCount || 0)}>
                    {selectedMarker.merchant.visitCount || 0}
                  </Tag>
                </Col>
              </Row>
              {selectedMarker.merchant.transactions &&
                selectedMarker.merchant.transactions.length > 0 && (
                  <>
                    <Divider style={{ margin: "8px 0" }} />
                    <Text type="secondary" strong>
                      Transaction Timeline:
                    </Text>
                    <Space
                      direction="vertical"
                      size="small"
                      style={{ width: "100%" }}
                    >
                      {selectedMarker.merchant.transactions
                        .sort(
                          (a: { transactionTime: string; amount: number }, b: { transactionTime: string; amount: number }) =>
                            new Date(a.transactionTime).getTime() -
                            new Date(b.transactionTime).getTime()
                        )
                        .map((tx: { transactionTime: string; amount: number }, txIdx: number) => (
                          <div
                            key={txIdx}
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                              padding: "4px 8px",
                              backgroundColor: "#f5f5f5",
                              borderRadius: 4,
                            }}
                          >
                            <Text type="secondary" style={{ fontSize: "11px" }}>
                              {formatDateTime(tx.transactionTime)}
                            </Text>
                            <Text strong style={{ color: "#3f8600" }}>
                              {formatCurrency(tx.amount)}
                            </Text>
                          </div>
                        ))}
                    </Space>
                  </>
                )}
            </Space>
          </Card>
        )}
      </div>
    );
  }

  // Render actual Google Map
  const mapOptions = {
    disableDefaultUI: false,
    zoomControl: true,
    streetViewControl: false,
    mapTypeControl: false,
    fullscreenControl: true,
  };

  return (
    <div className={styles.map_container}>
      <GoogleMap
        mapContainerStyle={{ width: "100%", height: "600px", borderRadius: "8px" }}
        center={mapCenter}
        zoom={merchants.filter(m => m.merchant.coordinates).length === 1 ? 15 : 12}
        options={mapOptions}
      >
        {merchants
          .filter((item) => item.merchant.coordinates !== null)
          .map((item, idx) => {
            if (!item.merchant.coordinates) return null;
            
            const markerColor = getMarkerColor(item.merchant.visitCount || 0);
            const isSelected = selectedMarker?.merchant.businessId === item.merchant.businessId;

            return (
              <Marker
                key={`marker-${item.merchant.businessId}-${idx}`}
                position={item.merchant.coordinates}
                onClick={() => handleMarkerClick(item)}
                icon={{
                  path: google.maps.SymbolPath.CIRCLE,
                  scale: 8,
                  fillColor: markerColor,
                  fillOpacity: 1,
                  strokeColor: "#fff",
                  strokeWeight: 2,
                }}
              >
              {isSelected && (
                <InfoWindow
                  onCloseClick={() => setSelectedMarker(null)}
                  position={item.merchant.coordinates}
                >
                  <div style={{ maxWidth: "300px" }}>
                    <Space direction="vertical" size="small">
                      <Text strong>{item.merchant.businessTradeName}</Text>
                      {item.merchant.city && (
                        <Tag icon={<EnvironmentOutlined />} color="blue">
                          {item.merchant.city}
                        </Tag>
                      )}
                      <div>
                        <Text type="secondary">Transactions: </Text>
                        <Text strong>{item.merchant.transactionCount}</Text>
                      </div>
                      <div>
                        <Text type="secondary">Total: </Text>
                        <Text strong style={{ color: "#3f8600" }}>
                          {formatCurrency(item.merchant.totalAmount)}
                        </Text>
                      </div>
                      <div>
                        <Text type="secondary">Frequency: </Text>
                        <Tag color={markerColor}>
                          {item.merchant.visitCount || 0} visits
                        </Tag>
                      </div>
                    </Space>
                  </div>
                </InfoWindow>
              )}
            </Marker>
          );
        })}

        {/* Journey path for selected consumer */}
        {showJourneyPath && 
         selectedConsumer && 
         merchantsByConsumer.has(selectedConsumer) && 
         (() => {
           const consumerMerchants = merchantsByConsumer.get(selectedConsumer);
           if (!consumerMerchants || consumerMerchants.length < 2) return null;
           
           const path = consumerMerchants
             .sort((a, b) => {
               const aTime = a.merchant.firstVisit ? new Date(a.merchant.firstVisit).getTime() : 0;
               const bTime = b.merchant.firstVisit ? new Date(b.merchant.firstVisit).getTime() : 0;
               return aTime - bTime;
             })
             .map(m => m.merchant.coordinates)
             .filter((coord): coord is { lat: number; lng: number } => coord !== null);
           
           if (path.length < 2) return null;
           
           return (
             <Polyline
               path={path}
               options={{
                 strokeColor: "#1890ff",
                 strokeOpacity: 0.6,
                 strokeWeight: 3,
                 geodesic: true,
               }}
             />
           );
         })()}
      </GoogleMap>

      {/* Merchant list sidebar */}
      <div className={styles.markers_list}>
        <Title level={5}>Merchant Locations ({merchants.length})</Title>
        <Space
          direction="vertical"
          size="small"
          style={{ width: "100%", maxHeight: "500px", overflowY: "auto" }}
        >
          {merchants.map((item, idx) => {
            const { consumer, merchant } = item;
            if (!merchant.coordinates) return null;
            
            const markerColor = getMarkerColor(merchant.visitCount || 0);
            const isSelected = selectedMarker?.merchant.businessId === merchant.businessId;

            return (
              <Card
                key={idx}
                size="small"
                style={{
                  marginBottom: 8,
                  border: isSelected ? `2px solid ${markerColor}` : undefined,
                  cursor: "pointer",
                }}
                onClick={() => handleMarkerClick(item)}
                hoverable
              >
                <div className={styles.merchant_card_mobile}>
                  {/* First line: Name only */}
                  <div className={styles.merchant_name_line}>
                    <div
                      className={styles.marker_dot}
                      style={{
                        backgroundColor: markerColor,
                      }}
                    />
                    <Tooltip title={merchant.businessTradeName}>
                      <Text strong className={styles.merchant_name_full}>
                        {merchant.businessTradeName}
                      </Text>
                    </Tooltip>
                  </div>

                  {/* Second line: City and Visit count */}
                  <div className={styles.merchant_meta_line}>
                    {merchant.city && (
                      <Tag icon={<EnvironmentOutlined />} color="blue" className={styles.city_tag}>
                        {merchant.city}
                      </Tag>
                    )}
                    <Tag color={markerColor} className={styles.visit_count_tag}>
                      {merchant.visitCount || 0} visits
                    </Tag>
                  </div>

                  {/* Rest: Coordinates, Transactions, etc. */}
                  <Row gutter={[8, 4]}>
                    <Col span={24}>
                      <Text type="secondary" style={{ fontSize: "11px" }}>
                        📍 {merchant.coordinates.lat.toFixed(6)},{" "}
                        {merchant.coordinates.lng.toFixed(6)}
                      </Text>
                    </Col>
                    <Col span={12}>
                      <Text type="secondary">Transactions:</Text>
                      <br />
                      <Text strong>{merchant.transactionCount}</Text>
                    </Col>
                    <Col span={12}>
                      <Text type="secondary">Total:</Text>
                      <br />
                      <Text strong style={{ color: "#3f8600" }}>
                        {formatCurrency(merchant.totalAmount)}
                      </Text>
                    </Col>
                  </Row>
                </div>
              </Card>
            );
          })}
        </Space>
      </div>

      {/* Selected marker details - Only show if InfoWindow is not visible (no coordinates or InfoWindow closed) */}
      {selectedMarker && !selectedMarker.merchant.coordinates && (
        <Card
          className={styles.marker_details}
          title={
            <Space>
              <ShopOutlined />
              <Text strong>{selectedMarker.merchant.businessTradeName}</Text>
            </Space>
          }
          extra={
            <Button
              type="text"
              onClick={() => setSelectedMarker(null)}
              size="small"
            >
              Close
            </Button>
          }
        >
          <Space direction="vertical" size="middle" style={{ width: "100%" }}>
            <div>
              <Text type="secondary">Business Name:</Text>
              <br />
              <Text strong>{selectedMarker.merchant.businessName}</Text>
            </div>
            <div>
              <Text type="secondary">Location:</Text>
              <br />
              <Text>
                {selectedMarker.merchant.city && (
                  <Tag icon={<EnvironmentOutlined />}>
                    {selectedMarker.merchant.city}
                  </Tag>
                )}
                {selectedMarker.merchant.address && (
                  <Text type="secondary" style={{ fontSize: "12px" }}>
                    {[
                      selectedMarker.merchant.address.street,
                      selectedMarker.merchant.address.houseNumber,
                    ]
                      .filter(Boolean)
                      .join(" ")}
                  </Text>
                )}
              </Text>
            </div>
            <Row gutter={16}>
              <Col span={12}>
                <Text type="secondary">Transactions:</Text>
                <br />
                <Text strong>{selectedMarker.merchant.transactionCount}</Text>
              </Col>
              <Col span={12}>
                <Text type="secondary">Total Amount:</Text>
                <br />
                <Text strong style={{ color: "#3f8600" }}>
                  {formatCurrency(selectedMarker.merchant.totalAmount)}
                </Text>
              </Col>
              <Col span={12}>
                <Text type="secondary">Avg Transaction:</Text>
                <br />
                <Text>
                  {formatCurrency(
                    selectedMarker.merchant.averageTransactionValue
                  )}
                </Text>
              </Col>
              <Col span={12}>
                <Text type="secondary">Visits:</Text>
                <br />
                <Tag color={getMarkerColor(selectedMarker.merchant.visitCount || 0)}>
                  {selectedMarker.merchant.visitCount || 0}
                </Tag>
              </Col>
            </Row>
          </Space>
        </Card>
      )}
    </div>
  );
}

